import { useEffect } from 'react';

/**
 * Injecte les données structurées Schema.org (JSON-LD) + meta SEO pour la page d'avis.
 * Injection DOM directe (robuste sur CRA, contrairement à react-helmet pour les <script>).
 * - Dates au format ISO 8601 (obligatoire pour l'éligibilité Google Rich Results)
 * - Valeurs numériques cohérentes
 * - Champs recommandés : address (PostalAddress), telephone, priceRange, datePublished
 * NB : FairViews est une plateforme d'avis tierce -> aggregateRating légitime ici.
 */

const toIsoDate = (val) => {
  if (!val) return null;
  const d = new Date(val);
  if (isNaN(d.getTime())) return null;
  return d.toISOString().split('T')[0];
};

const upsertMeta = (attr, key, content) => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const SchemaOrgReviews = ({ company, reviews = [] }) => {
  useEffect(() => {
    if (!company) return;

    const trustScore = Number(company.stats?.trust_score || 0);
    const totalReviews = Number(company.stats?.total_reviews || reviews.length || 0);
    const siteUrl = company.website || `https://${company.domain}`;

    const reviewSchemas = reviews
      .slice(0, 5)
      .map((review) => {
        const body = review.content || review.title;
        if (!body) return null;
        const datePublished = toIsoDate(review.created_at) || toIsoDate(review.date);
        return {
          '@type': 'Review',
          author: { '@type': 'Person', name: review.author || 'Client vérifié' },
          name: review.title || '',
          reviewBody: body,
          ...(datePublished ? { datePublished } : {}),
          reviewRating: { '@type': 'Rating', ratingValue: Number(review.rating) || 5, bestRating: 5, worstRating: 1 },
        };
      })
      .filter(Boolean);

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: company.name,
      url: siteUrl,
      priceRange: '€€',
      ...(company.phone ? { telephone: company.phone } : {}),
      ...(company.address
        ? { address: { '@type': 'PostalAddress', streetAddress: company.address, addressCountry: 'FR' } }
        : {}),
      ...(totalReviews > 0
        ? {
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: Number(trustScore.toFixed(1)),
              reviewCount: totalReviews,
              bestRating: 5,
              worstRating: 1,
            },
          }
        : {}),
      ...(reviewSchemas.length > 0 ? { review: reviewSchemas } : {}),
    };

    // JSON-LD
    let ld = document.head.querySelector('script[data-fairviews-jsonld]');
    if (!ld) {
      ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.setAttribute('data-fairviews-jsonld', '');
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(schema);

    // Meta / titre SEO
    const pageTitle = `${company.name} - Avis clients (${trustScore.toFixed(1)}/5, ${totalReviews} avis) | FairViews`;
    const pageDescription = `Découvrez les ${totalReviews} avis clients de ${company.name} (${company.category || 'entreprise'}). Note moyenne de ${trustScore.toFixed(1)}/5 sur FairViews.`;
    const canonicalUrl = `https://fairviews.com/review/${company.domain}`;

    document.title = pageTitle;
    upsertMeta('name', 'description', pageDescription);
    upsertMeta('name', 'robots', 'index, follow');
    upsertMeta('property', 'og:title', pageTitle);
    upsertMeta('property', 'og:description', pageDescription);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:url', canonicalUrl);
    upsertLink('canonical', canonicalUrl);
  }, [company, reviews]);

  return null;
};

/**
 * Génère le JSON-LD (string) pour injection côté serveur/widgets.
 * schemaType: 'business' (LocalBusiness) ou 'product' (Product -> étoiles éligibles côté site client).
 */
export const generateSchemaJsonLd = (company, reviews = [], schemaType = 'business') => {
  if (!company) return '';

  const trustScore = Number(company.trust_score || company.stats?.trust_score || 0);
  const totalReviews = Number(company.total_reviews || company.stats?.total_reviews || reviews.length || 0);

  const aggregateRating =
    totalReviews > 0
      ? {
          '@type': 'AggregateRating',
          ratingValue: Number(trustScore.toFixed(1)),
          reviewCount: totalReviews,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;

  const reviewSchemas = reviews
    .slice(0, 5)
    .map((review) => {
      const body = review.content || review.title;
      if (!body) return null;
      const datePublished = toIsoDate(review.created_at) || toIsoDate(review.date);
      return {
        '@type': 'Review',
        author: { '@type': 'Person', name: review.author || 'Client vérifié' },
        name: review.title || '',
        reviewBody: body,
        ...(datePublished ? { datePublished } : {}),
        reviewRating: { '@type': 'Rating', ratingValue: Number(review.rating) || 5, bestRating: 5, worstRating: 1 },
      };
    })
    .filter(Boolean);

  let schema;
  if (schemaType === 'product') {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: company.category || company.name,
      brand: { '@type': 'Brand', name: company.name },
      ...(aggregateRating ? { aggregateRating } : {}),
    };
  } else {
    schema = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: company.name,
      url: company.website || `https://${company.domain}`,
      priceRange: '€€',
      ...(aggregateRating ? { aggregateRating } : {}),
    };
  }

  if (reviewSchemas.length > 0) schema.review = reviewSchemas;

  return JSON.stringify(schema);
};

export default SchemaOrgReviews;
